// services/frontend/src/hooks/useBatchUploadStandalone.ts
// Standalone batch upload hook for My Photos (without collection logic)
// Shares progress indicator code with useBatchUpload

import { useReducer, useEffect, useCallback, useRef } from 'react';
import { usePhotoUpload } from './usePhotoUpload';
import type {
  FileUploadItem,
  BatchUploadState,
  UploadAction,
  UseBatchUploadReturn
} from '@/types/upload';
import {
  calculateBatchState,
  getNextFilesToStart,
  createFileUploadItem,
  MAX_CONCURRENT_UPLOADS
} from '@/utils/uploadQueue';
import { filterValidFiles, generatePhotoTitle } from '@/utils/fileValidation';
import logger from '@/utils/logger';

/**
 * Reducer function for batch upload state management
 * Identical to useBatchUpload reducer - shared logic
 */
function uploadReducer(state: BatchUploadState, action: UploadAction): BatchUploadState {
  let newFiles: FileUploadItem[];

  switch (action.type) {
    case 'ADD_FILES': {
      const { valid } = filterValidFiles(action.payload.files);
      const newItems = valid.map(file => createFileUploadItem(file));

      newFiles = [...state.files, ...newItems];
      return calculateBatchState(newFiles);
    }

    case 'ADD_ITEMS': {
      // Add pre-created items (used by useBatchUpload for position tracking)
      newFiles = [...state.files, ...action.payload.items];
      return calculateBatchState(newFiles);
    }

    case 'START_UPLOAD': {
      newFiles = state.files.map(file =>
        file.id === action.payload.id
          ? { ...file, state: 'uploading' as const, startedAt: action.payload.startedAt }
          : file
      );
      return calculateBatchState(newFiles);
    }

    case 'UPDATE_PROGRESS': {
      newFiles = state.files.map(file =>
        file.id === action.payload.id
          ? { ...file, progress: Math.min(100, Math.max(0, action.payload.progress)) }
          : file
      );
      return calculateBatchState(newFiles);
    }

    case 'COMPLETE_UPLOAD': {
      newFiles = state.files.map(file =>
        file.id === action.payload.id
          ? {
              ...file,
              state: 'success' as const,
              photoId: action.payload.photoId,
              completedAt: action.payload.completedAt,
              progress: 100
            }
          : file
      );
      return calculateBatchState(newFiles);
    }

    case 'FAIL_UPLOAD': {
      newFiles = state.files.map(file =>
        file.id === action.payload.id
          ? {
              ...file,
              state: 'error' as const,
              error: action.payload.error,
              completedAt: action.payload.completedAt
            }
          : file
      );
      return calculateBatchState(newFiles);
    }

    case 'RETRY_UPLOAD': {
      newFiles = state.files.map(file =>
        file.id === action.payload.id && file.state === 'error'
          ? {
              ...file,
              state: 'pending' as const,
              error: null,
              progress: 0,
              startedAt: null,
              completedAt: null
            }
          : file
      );
      return calculateBatchState(newFiles);
    }

    case 'REMOVE_FILE': {
      newFiles = state.files.filter(
        file => file.id !== action.payload.id || file.state === 'uploading'
      );
      return calculateBatchState(newFiles);
    }

    default:
      return state;
  }
}

const initialState: BatchUploadState = calculateBatchState([]);

/**
 * Custom hook for managing batch photo uploads to My Photos
 * Similar to useBatchUpload but without collection logic
 *
 * @param onUploadComplete - Optional callback fired when all uploads complete
 * @returns Batch upload state and control functions
 */
export function useBatchUploadStandalone(
  onUploadComplete?: () => void
): UseBatchUploadReturn {
  const [state, dispatch] = useReducer(uploadReducer, initialState);
  const { uploadPhoto } = usePhotoUpload();

  // Track active upload promises to manage cleanup
  const activeUploadsRef = useRef<Map<string, AbortController>>(new Map());
  const prevIsCompleteRef = useRef(false);

  /**
   * Process an individual file upload
   * Simpler than useBatchUpload - just uploads, no collection addition
   */
  const processUpload = useCallback(
    async (item: FileUploadItem) => {
      const abortController = new AbortController();
      activeUploadsRef.current.set(item.id, abortController);

      logger.log('BatchUploadStandalone', 'debug', `Starting upload for item: ${item.filename}`, {
        itemId: item.id,
        filename: item.filename,
        fileSize: item.file.size
      });

      try {
        // Generate title from filename
        const title = generatePhotoTitle(item.filename);

        // Upload photo
        logger.log('BatchUploadStandalone', 'debug', `Calling uploadPhoto for: ${item.filename}`, { title });
        const result = await uploadPhoto({
          title,
          file: item.file
        });

        logger.log('BatchUploadStandalone', 'debug', `uploadPhoto returned for: ${item.filename}`, {
          success: result.success,
          photoId: result.photoId,
          error: result.error
        });

        if (abortController.signal.aborted) {
          logger.log('BatchUploadStandalone', 'warn', `Upload aborted for: ${item.filename}`, { itemId: item.id });
          return;
        }

        if (!result.success || !result.photoId) {
          logger.log('BatchUploadStandalone', 'error', `Upload failed for: ${item.filename}`, {
            itemId: item.id,
            error: result.error,
            success: result.success,
            photoId: result.photoId
          });
          throw new Error(result.error || 'Upload failed');
        }

        const photoId = result.photoId;
        logger.log('BatchUploadStandalone', 'info', `Photo uploaded successfully: ${item.filename}`, {
          photoId,
          itemId: item.id
        });

        // Mark upload as complete
        dispatch({
          type: 'COMPLETE_UPLOAD',
          payload: {
            id: item.id,
            photoId,
            completedAt: new Date()
          }
        });
      } catch (error) {
        if (abortController.signal.aborted) {
          logger.log('BatchUploadStandalone', 'debug', `Upload already aborted for: ${item.filename}`, { itemId: item.id });
          return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Upload failed';

        logger.log('BatchUploadStandalone', 'error', `Upload failed for: ${item.filename}`, {
          itemId: item.id,
          error,
          errorMessage
        });

        dispatch({
          type: 'FAIL_UPLOAD',
          payload: {
            id: item.id,
            error: errorMessage,
            completedAt: new Date()
          }
        });
      } finally {
        activeUploadsRef.current.delete(item.id);
        logger.log('BatchUploadStandalone', 'debug', `Cleaned up upload for: ${item.filename}`, { itemId: item.id });
      }
    },
    [uploadPhoto]
  );

  /**
   * Queue processor effect - starts pending uploads when slots available
   */
  useEffect(() => {
    const filesToStart = getNextFilesToStart(state.files, MAX_CONCURRENT_UPLOADS);

    if (filesToStart.length > 0) {
      logger.log('BatchUploadStandalone', 'debug', `Starting ${filesToStart.length} uploads from queue`, {
        concurrentUploads: state.activeCount,
        pendingUploads: state.pendingCount,
        maxConcurrent: MAX_CONCURRENT_UPLOADS,
        filenames: filesToStart.map(f => f.filename)
      });

      // Start each pending upload
      filesToStart.forEach(file => {
        dispatch({
          type: 'START_UPLOAD',
          payload: {
            id: file.id,
            startedAt: new Date()
          }
        });

        logger.log('BatchUploadStandalone', 'debug', `Dispatched START_UPLOAD for: ${file.filename}`, { fileId: file.id });

        // Process upload asynchronously
        processUpload(file);
      });
    }
  }, [state.files, processUpload]);

  /**
   * Fire completion callback when batch completes
   */
  useEffect(() => {
    if (state.isComplete && !prevIsCompleteRef.current && state.totalCount > 0) {
      logger.info('BatchUploadStandalone', 'Batch upload completed', {
        totalCount: state.totalCount,
        completedCount: state.completedCount,
        failedCount: state.failedCount
      });
      onUploadComplete?.();
    }
    prevIsCompleteRef.current = state.isComplete;
  }, [state.isComplete, state.totalCount, state.completedCount, state.failedCount, onUploadComplete]);

  /**
   * Cleanup effect - cancel active uploads on unmount
   */
  useEffect(() => {
    return () => {
      activeUploadsRef.current.forEach(controller => {
        controller.abort();
      });
      activeUploadsRef.current.clear();
    };
  }, []);

  /**
   * Add files to the upload queue
   */
  const addFiles = useCallback((files: File[]) => {
    if (files.length >= 50) {
      logger.log(
        'BatchUploadStandalone',
        'warn',
        `Large batch detected: ${files.length} files. ` +
        `This may take some time and consume significant bandwidth.`
      );
    }

    dispatch({
      type: 'ADD_FILES',
      payload: { files }
    });
  }, []);

  /**
   * Retry a failed upload
   */
  const retryUpload = useCallback((id: string) => {
    dispatch({
      type: 'RETRY_UPLOAD',
      payload: { id }
    });
  }, []);

  /**
   * Remove a file from the queue
   */
  const removeFile = useCallback((id: string) => {
    dispatch({
      type: 'REMOVE_FILE',
      payload: { id }
    });
  }, []);

  /**
   * Clear all completed and failed uploads from the queue
   */
  const clearCompleted = useCallback(() => {
    const completedAndFailed = state.files.filter(
      f => f.state === 'success' || f.state === 'error'
    );

    completedAndFailed.forEach(file => {
      dispatch({
        type: 'REMOVE_FILE',
        payload: { id: file.id }
      });
    });
  }, [state.files]);

  return {
    state,
    addFiles,
    retryUpload,
    removeFile,
    clearCompleted
  };
}
